import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; 
import UniversalLookup from './UniversalLookup';
import AuditHistoryModal from './AuditHistoryModal';
import { Truck, PackageCheck, ClipboardCheck, AlertTriangle, ArrowLeft, ChevronRight, List, Search, MapPin, ShieldAlert, Plus, Trash2 } from 'lucide-react';

const ReceivingDetails = () => {
  const { ponum } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [lines, setLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLines, setLoadingLines] = useState(true);
  
  const [distributions, setDistributions] = useState([
    { location: '', quantity: 0, rejected_qty: 0, bin: '' }
  ]);

  const [receiptData, setReceiptData] = useState({
    inspection_status: 'NONE',
    remarks: ''
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  const [showAudit, setShowAudit] = useState(false);
  const session = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const fetchLines = useCallback(async () => {
    if (!ponum || ponum === 'undefined') return;
    setLoadingLines(true);
    try {
      const res = await axios.get(`/api/po/${ponum}/lines`);
      setLines(res.data.filter(l => (l.qty - (l.received_qty || 0)) > 0));
    } catch (err) {
      toast.error(t('receivingDetails.errLoadLines')); // 🎯
    } finally {
      setLoadingLines(false);
    }
  }, [ponum, t]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const addDistribution = () => setDistributions([...distributions, { location: '', quantity: 0, rejected_qty: 0, bin: '' }]);
  const removeDistribution = (idx) => distributions.length > 1 && setDistributions(distributions.filter((_, i) => i !== idx));
  const updateDist = (idx, field, val) => {
    const newDist = [...distributions];
    newDist[idx][field] = val;
    setDistributions(newDist);
  };

  const handleLookupSelect = (row) => {
    const val = row.location || row.value;
    updateDist(lookup.targetIdx, 'location', val);
    setLookup({ ...lookup, isOpen: false });
  };

  const stats = useMemo(() => {
    const totalArrived = distributions.reduce((sum, d) => sum + parseFloat(d.quantity || 0), 0);
    const totalRejected = distributions.reduce((sum, d) => sum + parseFloat(d.rejected_qty || 0), 0);
    const netAccepted = totalArrived - totalRejected;
    return { totalArrived, totalRejected, netAccepted };
  }, [distributions]);

  const handleConfirmReceipt = async (forceAutoAssign = false) => {
    if (distributions.some(d => !d.location || d.quantity <= 0)) return toast.error(t('receivingDetails.errMissingDist')); // 🎯
    if (selectedLine.inspection_required && (receiptData.inspection_status === 'NONE' || !receiptData.inspection_status)) {
        return toast.error(t('receivingDetails.errInspMandatory')); // 🎯
    }
    if (stats.netAccepted < 0) return toast.error(t('receivingDetails.errRejectExceeds')); // 🎯

    setLoading(true);
    try {
      const payload = {
        ponum,
        itemnum: selectedLine.itemnum,
        org_id: session.org_id,
        site_id: session.site_id,
        actualcost: selectedLine.unitcost,
        inspection_status: receiptData.inspection_status,
        distributions,
        auto_assign: forceAutoAssign
      };

      const res = await axios.post('/api/inventory/receive-items-bulk', payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(t('receivingDetails.msgSuccess')); // 🎯
      
      if (res.data.docs_closed?.length > 0) {
        toast.success(`${t('receivingDetails.msgAutoClosed')} ${res.data.docs_closed.join(', ')}`, { icon: '📦', duration: 5000 }); // 🎯
      }

      setSelectedLine(null);
      setDistributions([{ location: '', quantity: 0, rejected_qty: 0, bin: '' }]);
      setReceiptData({ inspection_status: 'NONE', remarks: '' });
      fetchLines();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      
      if (detail.includes("404_NOT_ASSIGNED")) {
        const locName = detail.split(":")[1];
        Swal.fire({
          title: t('receivingDetails.swalAssignTitle'), // 🎯
          text: t('receivingDetails.swalAssignText', { item: selectedLine.itemnum, loc: locName }), 
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1a3353',
          cancelButtonColor: '#d33',
          confirmButtonText: t('receivingDetails.swalAssignBtn') // 🎯
        }).then((result) => {
          if (result.isConfirmed) handleConfirmReceipt(true);
        });
      } else {
        toast.error(detail || t('receivingDetails.errUnexpected')); // 🎯
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex flex-col h-screen bg-surface-app text-[11px] ${isRTL ? 'font-cairo' : 'font-sans'}`}>
      <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="receiving" recordId={ponum} />

      {/* Header Bar */}
      <div className="bg-surface-card p-2 border-b flex justify-between items-center shadow-sm z-20 font-bold uppercase tracking-tighter italic">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/receiving')} className="p-1 hover:bg-surface-panel rounded-full text-primary-dark">
              <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <div className={`flex flex-col px-4 text-primary-dark ${isRTL ? 'border-l' : 'border-r'}`}>
            <label className="text-[8px] text-ink-secondary font-black">{t('receivingDetails.hubTitle')}</label>
            <span>{t('receivingDetails.poRef')} {ponum}</span>
          </div>
        </div>
        <button onClick={() => handleConfirmReceipt(false)} disabled={!selectedLine || loading}
                className="bg-primary-dark text-white px-10 py-2 rounded font-black flex items-center gap-2 shadow-lg hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
           <PackageCheck size={16}/> {loading ? t('receivingDetails.btnExecuting') : t('receivingDetails.btnConfirm')}
        </button>
      </div>

      <div className="flex-1 p-6 grid grid-cols-12 gap-6 overflow-hidden">
        {/* Left Table: Items */}
        <div className="col-span-5 bg-surface-card border rounded shadow-2xl overflow-auto border-t-4 border-t-primary-dark">
          <div className="p-3 bg-surface-panel border-b font-black text-primary-dark uppercase flex items-center gap-2 tracking-widest text-[9px]">
            <List size={14}/> {t('receivingDetails.tblItemsPending')}
          </div>
          <table className="w-full text-left">
            <thead className="bg-surface-panel border-b text-ink-secondary font-black uppercase">
              <tr>
                  <th className={`p-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('receivingDetails.colItemDetails')}</th>
                  <th className={`p-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('receivingDetails.colBalance')}</th>
              </tr>
            </thead>
            <tbody>
              {!loadingLines && lines.map((l, i) => (
                <tr key={i} onClick={() => setSelectedLine(l)}
                    className={`border-b hover:bg-primary-soft cursor-pointer transition-all ${selectedLine?.itemnum === l.itemnum ? 'bg-primary-soft scale-[1.01] shadow-inner' : ''}`}>
                  <td className="p-3 font-black text-primary-dark uppercase">
                    {l.itemnum}
                    <span className="block text-[9px] font-normal text-ink-secondary italic leading-tight">{l.description}</span>
                    {l.inspection_required && (
                        <span className="mt-1 inline-flex items-center gap-1 text-[7px] bg-status-warning-bg text-status-warning px-2 py-0.5 rounded font-black tracking-widest animate-pulse border border-status-warning-bg">
                            <ShieldAlert size={8}/> {t('receivingDetails.lblQualityCheck')}
                        </span>
                    )}
                  </td>
                  <td className={`p-3 font-black text-status-warning text-sm tracking-tighter ${isRTL ? 'text-left' : 'text-right'}`}>
                      {l.qty - (l.received_qty || 0)} <span className="text-[10px]">{t('receivingDetails.lblUnits')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loadingLines ? (
            <div className="p-20 text-center text-ink-secondary italic uppercase font-black tracking-widest animate-pulse">{t('po.messages.syncing', 'Syncing...')}</div>
          ) : lines.length === 0 && (
            <div className="p-20 text-center text-ink-secondary italic uppercase font-black tracking-widest">{t('receivingDetails.msgNoPending')}</div>
          )}
        </div>

        {/* Right Side Control: Distribution & Finance */}
        <div className="col-span-7 flex flex-col gap-4 overflow-y-auto pr-2">
          {selectedLine ? (
            <div className={`space-y-4 animate-in fade-in duration-300 ${isRTL ? 'slide-in-from-left' : 'slide-in-from-right'}`}>
              
              {/* PO Cost Reference (informational only - landed cost & final valuation now live on the Purchase Invoice, matched after receipt) */}
              <div className="bg-surface-card p-4 border rounded shadow-md flex justify-between items-center border-t-4 border-t-primary">
                  <span className="text-[9px] font-black text-ink-secondary uppercase italic tracking-widest">{t('receivingDetails.lblQuantityOnly', 'Physical receipt only - cost is finalized on the Purchase Invoice')}</span>
                  <div className={`bg-primary-soft p-3 rounded-sm border border-primary-soft ${isRTL ? 'text-left' : 'text-right'}`}>
                    <p className="text-[7px] font-black text-primary uppercase">{t('receivingDetails.lblBaseCost')}</p>
                    <p className="font-black text-primary text-sm">{selectedLine.unitcost.toLocaleString()} {selectedLine.currency || 'EGP'}</p>
                  </div>
              </div>

              {/* Distribution Grid */}
              <div className="bg-surface-card border rounded shadow-2xl overflow-hidden">
                <div className="p-3 bg-primary-dark text-white font-black uppercase flex justify-between items-center shadow-lg">
                  <span className="text-[10px] tracking-widest">{t('receivingDetails.lblStoreMatrix')}</span>
                  <button onClick={addDistribution} className="bg-white/10 hover:bg-white/20 p-1 px-4 rounded-full flex items-center gap-1 transition-all text-[8px] border border-white/20">
                    <Plus size={12}/> {t('receivingDetails.btnSplitStore')}
                  </button>
                </div>
                <table className="w-full">
                    <thead className="bg-surface-panel border-b text-[8px] font-black text-ink-secondary uppercase">
                        <tr>
                            <th className={`p-2 ${isRTL ? 'text-right' : 'text-left'}`}>{t('receivingDetails.colStoreroom')}</th>
                            <th className={`p-2 ${isRTL ? 'text-left' : 'text-right'}`}>{t('receivingDetails.colArrived')}</th>
                            <th className={`p-2 text-status-danger ${isRTL ? 'text-left' : 'text-right'}`}>{t('receivingDetails.colRejected')}</th>
                            <th className="p-2 w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {distributions.map((dist, idx) => (
                            <tr key={idx} className="border-b hover:bg-surface-panel transition-colors">
                                <td className="p-2 flex items-center gap-2">
                                    <input className={`bg-transparent border-b border-line-subtle outline-none font-black text-primary-dark w-full ${isRTL ? 'text-right' : 'text-left'}`}
                                           value={dist.location} readOnly placeholder={t('receivingDetails.plcClickSearch')} />
                                    <Search size={16} className="text-primary cursor-pointer hover:scale-125 transition-transform"
                                            onClick={() => setLookup({isOpen:true, title: t('receivingDetails.lookupSelectStore'), type:'storerooms', field:'location', targetIdx: idx, filters:{org_id:session.org_id}})}/>
                                </td>
                                <td className="p-2">
                                    <input type="number" className={`w-full bg-transparent outline-none font-black text-primary focus:bg-primary-soft ${isRTL ? 'text-left' : 'text-right'}`}
                                           value={dist.quantity} onChange={(e) => updateDist(idx, 'quantity', e.target.value)} />
                                </td>
                                <td className="p-2">
                                    <input type="number" className={`w-full bg-transparent outline-none font-bold text-status-danger focus:bg-status-danger-bg ${isRTL ? 'text-left' : 'text-right'}`}
                                           value={dist.rejected_qty} onChange={(e) => updateDist(idx, 'rejected_qty', e.target.value)} />
                                </td>
                                <td className="p-2 text-center">
                                    <button onClick={() => removeDistribution(idx)} className="text-ink-secondary hover:text-status-danger transition-colors"><Trash2 size={14}/></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>

              {/* Inspection Module */}
              {selectedLine.inspection_required && (
                <div className="bg-status-warning-bg p-4 border border-status-warning/30 rounded shadow-inner">
                  <label className="text-[9px] font-black text-status-warning uppercase flex items-center gap-2 mb-2 tracking-tighter">
                    <ShieldAlert size={14}/> {t('receivingDetails.lblQcMatrix')}
                  </label>
                  <select className="w-full p-2 border-2 border-status-warning/30 rounded font-black text-xs bg-surface-card outline-none focus:border-status-warning transition-all"
                          value={receiptData.inspection_status} onChange={e => setReceiptData({...receiptData, inspection_status: e.target.value})}>
                    <option value="NONE">{t('receivingDetails.optSelectDecision')}</option>
                    <option value="PASSED">{t('receivingDetails.optPassed')}</option>
                    <option value="FAILED">{t('receivingDetails.optFailed')}</option>
                    <option value="PARTIAL">{t('receivingDetails.optPartial')}</option>
                  </select>
                </div>
              )}

              {/* Quantity Summary Card */}
              <div className="bg-slate-900 text-white p-5 rounded-sm shadow-2xl border-l-4 border-l-blue-500 flex justify-between items-center transition-all">
                  <div className="flex flex-col">
                    <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest italic leading-loose">{t('receivingDetails.lblNetArrived')}</span>
                    <span className="text-xl font-black text-blue-400 flex items-center gap-2">
                        {stats.netAccepted} <small className="text-[10px] text-gray-400">{t('receivingDetails.lblUnits')}</small>
                    </span>
                  </div>
                  {stats.totalRejected > 0 && (
                    <div className={`text-right border-white/10 ${isRTL ? 'border-r pr-6' : 'border-l pl-6'}`}>
                      <span className="text-[8px] font-black text-gray-500 uppercase block italic tracking-tighter mb-1">{t('receivingDetails.colRejected')}</span>
                      <span className="text-lg font-black text-status-danger block tracking-widest">{stats.totalRejected} <small className="text-[10px] text-gray-400">{t('receivingDetails.lblUnits')}</small></span>
                    </div>
                  )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-ink-secondary italic border-2 border-dashed rounded-lg font-black uppercase opacity-20 transition-all">
                <Truck size={84} className="mb-6 animate-pulse"/> 
                <span className="text-sm">{t('receivingDetails.msgSelectLine')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceivingDetails;